import { React, useContext, useMemo, useState } from 'react';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, Typography } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Alerta, PopupDialog } from '../components/alerta';
import PageHeader from '../components/pageHeader';
import { PageIntro } from '../components/ui';
import styles from '../pages/css/menu.module.css';
import DadosUsuario from '../utils/context';
import { fetchFlask } from '../utils/fetchFlask';
import { getSectionByKey, getSectionPath, getVisibleItems, getVisibleSections, hasMenuAccess } from '../utils/menuSections';

const MenuCard = ({ description, icon: Icon, onClick, title }) => (
    <button type='button' className='ui-menu-card' onClick={onClick}>
        <span className='ui-menu-card-head'>
            <span className='ui-menu-card-title'>
                <Icon fontSize='small' color='primary' />
                <span>{title}</span>
            </span>
            <ChevronRightRoundedIcon fontSize='small' />
        </span>
        <p className='ui-menu-card-copy'>{description}</p>
    </button>
);

const Menu = () => {
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [textoDialog, setTextoDialog] = useState({});
    const [openAlerta, setOpenAlerta] = useState(false);

    const navigate = useNavigate();
    const { section } = useParams();
    const { autorizacoes = [] } = useContext(DadosUsuario);

    const visibleSections = useMemo(() => getVisibleSections(autorizacoes), [autorizacoes]);
    const selectedSection = section ? getSectionByKey(section) : null;
    const canAccessSelected = selectedSection && hasMenuAccess(selectedSection, autorizacoes);
    const visibleSelectedItems = useMemo(() =>
        selectedSection ? getVisibleItems(selectedSection.items, autorizacoes) : []
    , [selectedSection, autorizacoes]);
    const accessibleLinkRoutes = useMemo(() =>
        visibleSections.flatMap((sec) => getVisibleItems(sec.items, autorizacoes)
            .filter((item) => item.type === 'link')
            .map((item) => item.page))
    , [visibleSections, autorizacoes]);
    const selectedSectionSingleItem = visibleSelectedItems.length === 1 ? visibleSelectedItems[0] : null;

    function handleButtonClick (e, titulo, texto, funcao) {
        e.preventDefault();
        setOpenDialog(true);
        setTextoDialog({ titulo, texto, funcao });
    }

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    async function submitFaturasNeoenergia () {
        const response = await fetchFlask('lerFaturasNeoenergia');
        handleAlerta(response);
    }

    async function submitCSVPagar () {
        const response = await fetchFlask('gerarContasPagar');
        handleAlerta(response);
    }

    const getActionHandler = (item) => {
        if (item.action === 'lerFaturasNeoenergia') {
            return (e) => handleButtonClick(
                e,
                'Leitura de Faturas',
                'Tem certeza que deseja realizar a leitura de faturas?',
                submitFaturasNeoenergia
            );
        }

        if (item.action === 'gerarContasPagar') {
            return (e) => handleButtonClick(
                e,
                'CSV Pagar',
                'Tem certeza que deseja gerar o CSV de pagar e enviar por e-mail?',
                submitCSVPagar
            );
        }

        return null;
    };

    const getSectionTarget = (sec) => {
        const visibleItems = getVisibleItems(sec.items, autorizacoes);

        if (visibleItems.length === 1 && visibleItems[0].type === 'link') {
            return `/${visibleItems[0].page}`;
        }

        return getSectionPath(sec);
    };

    const getItemDescription = (item) => {
        if (item.type === 'link') {
            return 'Abrir página';
        }

        return 'Executar ação';
    };

    const renderSection = (sec) => {
        const Icon = sec.icon;
        return (
            <Box className={styles.column} key={sec.key} sx={{ width: '100%' }}>
                <PageHeader icon={Icon}>{sec.title}</PageHeader>
                <PageIntro eyebrow='Área' title={sec.title}>Escolha uma das páginas disponíveis para o seu perfil.</PageIntro>
                <div className='ui-card-grid'>
                    {sec.items.map((item) => {
                        if (!hasMenuAccess(item, autorizacoes)) {
                            return null;
                        }

                        const itemKey = item.type === 'link' ? `${sec.key}-${item.page}` : `${sec.key}-${item.action}`;
                        const handleClick = item.type === 'link'
                            ? () => navigate(`/${item.page}`)
                            : getActionHandler(item);
                        const ItemIcon = item.icon || Icon;

                        return (
                            <MenuCard
                                key={itemKey}
                                description={getItemDescription(item)}
                                icon={ItemIcon}
                                onClick={handleClick}
                                title={item.text}
                            />
                        );
                    })}
                </div>
            </Box>
        );
    };

    const renderHome = () => (
        <>
            <PageHeader>SETTA CORPORATIVO</PageHeader>
            <PageIntro eyebrow='Home' title='Setta Corporativo'>Acesse as áreas liberadas para o seu perfil.</PageIntro>
            <div className='ui-card-grid'>
                {visibleSections.map((sec) => (
                    <MenuCard
                        key={sec.key}
                        description={sec.description}
                        icon={sec.icon}
                        onClick={() => navigate(getSectionTarget(sec))}
                        title={sec.title}
                    />
                ))}
            </div>
        </>
    );

    if (!section && accessibleLinkRoutes.length === 1) {
        return <Navigate to={`/${accessibleLinkRoutes[0]}`} replace />;
    }

    if (section && canAccessSelected && selectedSection.landing) {
        return <Navigate to={getSectionPath(selectedSection)} replace />;
    }

    if (section && canAccessSelected && selectedSectionSingleItem?.type === 'link') {
        return <Navigate to={`/${selectedSectionSingleItem.page}`} replace />;
    }

    return (
        <Box className={styles.row}>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PopupDialog
                titulo={textoDialog.titulo}
                texto={textoDialog.texto}
                open={openDialog}
                handleClose={() => setOpenDialog(false)}
                funcao={textoDialog.funcao}
            />

            <Box className={styles.column} sx={{ flex: 1 }}>
                {!section && renderHome()}
                {section && canAccessSelected && renderSection(selectedSection)}
                {section && !canAccessSelected && (
                    <Box>
                        <Typography variant='h5'>Area nao encontrada</Typography>
                        <Typography color='text.secondary' sx={{ mt: 1 }}>Escolha uma area valida no menu lateral.</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Menu;
